/**
 * CinematicOverlay — the visible layer of the "Simulează o comandă" cinematic.
 *
 * Two pieces, both driven entirely by DemoHost:
 *   • <CinematicStageView>  — a dim, focused stage for the beats that need
 *     bespoke visuals (intro title, WhatsApp phone, message→comandă transform,
 *     the delivery route, the issued invoice, outro). For the "live app reacts"
 *     beats DemoHost passes `stage={null}` and this renders nothing, so the real
 *     screen shows through with only the caption band floating.
 *   • <CinematicCaption>   — the always-on narration band with pause / skip /
 *     close controls and a chapter progress readout.
 *
 * Warm-editorial to match the app: bone card, marigold amber accents, Fraunces
 * display numerals, JetBrains-mono figures.
 */

import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Pause,
  Play,
  ShieldCheck,
  SkipForward,
  Sparkles,
  Truck,
  Wand2,
  X,
} from 'lucide-react'
import type { RefObject } from 'react'

import { Invoice } from '@/components/Invoice'
import { type PhoneBubble, PhoneMock } from '@/components/PhoneMock'
import { RouteMap, type RouteMapHandle, type RouteStop } from '@/components/RouteMap'
import type { ComandaLinie, Produs } from '@/db/dexie'
import { getComanda, getFactura, getProduse } from '@/db/queries'
import { CINEMATIC_CHAPTERS, type CinematicStage, DEMO_CMD_ID, STAR_TEXT } from '@/lib/demoScript'
import { azi, fmtKg, fmtOra, fmtRON, fmtSuma } from '@/lib/format'
import { cn } from '@/lib/utils'

const pad2 = (n: number) => String(n).padStart(2, '0')
const EASE = [0.16, 1, 0.3, 1] as const

// ===========================================================================
//  Stage view
// ===========================================================================
export interface CinematicStageViewProps {
  stage: CinematicStage | null
  routeRef: RefObject<RouteMapHandle | null>
  onRouteArrive: (index: number, stop: RouteStop) => void
  onRouteComplete: () => void
}

export function CinematicStageView({
  stage,
  routeRef,
  onRouteArrive,
  onRouteComplete,
}: CinematicStageViewProps) {
  // Rendered conditionally (no AnimatePresence exit): when a live beat sets
  // stage=null the backdrop unmounts INSTANTLY, snapping to reveal the real
  // screen reacting. Only entrance animations play — a stalled exit (throttled
  // rAF, slow device, rapid skipping) could otherwise leave the dim hanging
  // over the app.
  if (!stage) return null
  return (
    <motion.div
      key="cinematic-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="pointer-events-auto fixed inset-0 z-[92] flex items-center justify-center overflow-y-auto px-4 pt-10 pb-44"
      style={{ background: 'oklch(0.24 0.024 60 / 0.68)', backdropFilter: 'blur(2px)' }}
    >
      {/* A single keyed entrance — no exit animation, so the stage always
          tracks the current beat. */}
      <motion.div
        key={stage.kind}
        initial={{ opacity: 0, y: 16, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="mx-auto w-full max-w-[880px]"
      >
        {stage.kind === 'intro' && <IntroStage />}
        {stage.kind === 'phone' && <PhoneStage typing={stage.typing} />}
        {stage.kind === 'transform' && <TransformStage linii={stage.linii} />}
        {stage.kind === 'route' && (
          <RouteStage
            stops={stage.stops}
            routeRef={routeRef}
            onArrive={onRouteArrive}
            onComplete={onRouteComplete}
          />
        )}
        {stage.kind === 'invoice' && <InvoiceStage facturaId={stage.facturaId} />}
        {stage.kind === 'outro' && <OutroStage />}
      </motion.div>
    </motion.div>
  )
}

// --- intro -----------------------------------------------------------------
const FLOW_STEPS = ['Mesaj', 'Comandă', 'Livrare', 'Factură'] as const

function IntroStage() {
  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-hairline bg-card/95 px-8 py-10 text-center shadow-paper-lg">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 18 }}
        className="mx-auto grid size-14 place-items-center rounded-2xl bg-primary font-display text-2xl font-semibold text-primary-foreground shadow-paper"
        aria-hidden
      >
        E
      </motion.div>
      <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-ink">
        Exemplu SRL · Simulare
      </p>
      <h2 className="mt-2 font-display text-3xl font-medium tracking-tight text-foreground">
        De la mesaj la livrare
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
        O singură comandă, urmărită prin tot fluxul — automat, într-un singur loc.
      </p>
      <div className="mt-6 flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground">
        {FLOW_STEPS.map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className="rounded-full bg-amber-soft px-2.5 py-1 text-amber-ink">{s}</span>
            {i < FLOW_STEPS.length - 1 && <ArrowRight className="size-3 text-taupe" />}
          </span>
        ))}
      </div>
    </div>
  )
}

// --- phone (beat 1) --------------------------------------------------------
function PhoneStage({ typing }: { typing: boolean }) {
  const bubbles: PhoneBubble[] = typing
    ? []
    : [{ id: 'demo-in', side: 'in', text: STAR_TEXT, ora: fmtOra(azi()) }]
  return (
    <div className="flex justify-center">
      <PhoneMock
        nume="Măcelăria Crișul"
        subtitle={typing ? 'scrie…' : 'online'}
        bubbles={bubbles}
        typing={typing}
        convKey={typing ? 'demo-typing' : 'demo-msg'}
      />
    </div>
  )
}

// --- transform (beat 2): message → structured, priced order ----------------
function TransformStage({ linii }: { linii: ComandaLinie[] }) {
  const produse = useLiveQuery(getProduse, [], [] as Produs[])
  const catalog = new Map(produse.map((p) => [p.id, p]))
  const totalKg = linii.reduce((s, l) => s + l.cantitateKg, 0)
  const totalRON = linii.reduce((s, l) => s + l.cantitateKg * l.pretKg, 0)

  return (
    <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-hairline bg-card shadow-paper-lg">
      <div className="amber-wash flex items-center gap-2.5 border-b border-hairline px-5 py-4">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-amber-soft text-amber-ink">
          <Wand2 className="size-[18px]" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-lg font-medium leading-tight">Mesaj → comandă</h3>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Sparkles className="size-3.5 text-amber-ink" />
            Recunoaștere automată pe bază de reguli — fără AI, fără caiet.
          </p>
        </div>
      </div>

      <div className="grid gap-4 p-5 md:grid-cols-[1fr_auto_1.1fr] md:items-stretch">
        {/* the message */}
        <section className="flex flex-col rounded-xl border border-hairline bg-secondary/40 p-4">
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Mesajul primit
          </h4>
          <div className="rounded-2xl rounded-tl-sm bg-[#d9fdd3] px-3.5 py-2.5 text-[13.5px] leading-relaxed text-[#111b21] shadow-[0_1px_1px_rgba(0,0,0,0.12)]">
            {STAR_TEXT}
          </div>
          <p className="mt-2.5 text-xs text-muted-foreground">
            Text liber, prescurtat, cu „tăvi" în loc de kilograme.
          </p>
        </section>

        {/* arrow */}
        <div className="flex items-center justify-center">
          <motion.span
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="grid size-9 place-items-center rounded-full bg-amber text-amber-ink shadow-paper md:rotate-0 [transform:rotate(90deg)] md:[transform:none]"
          >
            <ArrowRight className="size-[18px]" />
          </motion.span>
        </div>

        {/* the structured order */}
        <section className="flex flex-col rounded-xl border border-amber/30 bg-card p-4 shadow-paper">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-pine/25 bg-pine-soft/60 px-3 py-2 text-sm"
          >
            <span className="grid size-5 place-items-center rounded-full bg-pine text-pine-foreground">
              <Check className="size-3.5" />
            </span>
            <span className="font-medium text-pine">Client recunoscut:</span>
            <span className="font-semibold text-foreground">Măcelăria Crișul</span>
            <span className="ml-auto rounded-md bg-amber-soft px-2 py-0.5 text-xs font-medium text-amber-ink">
              preț special
            </span>
          </motion.div>

          <div className="flex flex-col divide-y divide-hairline/70">
            {linii.map((l, i) => {
              const cat = catalog.get(l.produsId)
              const special = cat ? l.pretKg < cat.pretKg : false
              return (
                <motion.div
                  key={l.produsId}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.14, ease: EASE }}
                  className="grid grid-cols-[1fr_auto] items-center gap-2 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-medium leading-tight">{l.nume}</div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10.5px] text-muted-foreground nums">
                        {l.cod}
                      </span>
                      {special && (
                        <span className="rounded bg-amber-soft px-1 text-[9.5px] font-medium text-amber-ink">
                          preț special
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[13px] font-semibold text-foreground nums">
                      {fmtSuma(l.cantitateKg * l.pretKg)}
                    </div>
                    <div className="font-mono text-[10.5px] text-muted-foreground nums">
                      {fmtKg(l.cantitateKg)} × {fmtSuma(l.pretKg)}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 + linii.length * 0.14 }}
            className="mt-3 flex items-end justify-between border-t border-hairline pt-3"
          >
            <span className="text-xs text-muted-foreground">
              {linii.length} produse ·{' '}
              <span className="font-medium text-foreground">{fmtKg(totalKg)}</span>
            </span>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total</div>
              <div className="font-mono text-xl font-semibold text-amber-ink nums">
                {fmtRON(totalRON)}
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  )
}

// --- route (beat 5) --------------------------------------------------------
function RouteStage({
  stops,
  routeRef,
  onArrive,
  onComplete,
}: {
  stops: RouteStop[]
  routeRef: RefObject<RouteMapHandle | null>
  onArrive: (index: number, stop: RouteStop) => void
  onComplete: () => void
}) {
  return (
    <div className="mx-auto max-w-[820px] overflow-hidden rounded-2xl border border-hairline bg-card shadow-paper-lg">
      <div className="flex items-center gap-2.5 border-b border-hairline px-5 py-3.5">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-amber-soft text-amber-ink">
          <Truck className="size-[18px]" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-lg font-medium leading-tight">
            Traseu de livrare · Bihor
          </h3>
          <p className="text-xs text-muted-foreground">
            Camion frigorific · pornire din depozitul Oradea
          </p>
        </div>
      </div>
      <div className="p-4">
        <RouteMap
          ref={routeRef}
          stops={stops}
          showControls={false}
          onArrive={onArrive}
          onComplete={onComplete}
        />
      </div>
    </div>
  )
}

// --- invoice (beat 6) ------------------------------------------------------
function InvoiceStage({ facturaId }: { facturaId: string }) {
  const factura = useLiveQuery(() => getFactura(facturaId), [facturaId])
  const comanda = useLiveQuery(() => getComanda(DEMO_CMD_ID), [])

  return (
    <div className="mx-auto max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-hairline bg-card p-4 shadow-paper-lg sm:p-5">
      <div className="mb-3 flex items-center gap-2.5">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-pine-soft text-pine">
          <BadgeCheck className="size-[18px]" />
        </span>
        <div>
          <h3 className="font-display text-lg font-medium leading-tight">Factură emisă</h3>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5 text-pine" />
            e-Factura la ANAF · cod UIT pentru transportul de carne
          </p>
        </div>
      </div>
      {factura ? (
        <Invoice factura={factura} comanda={comanda} />
      ) : (
        <div className="grid place-items-center py-16 text-sm text-muted-foreground">
          Se emite factura…
        </div>
      )}
    </div>
  )
}

// --- outro -----------------------------------------------------------------
function OutroStage() {
  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-hairline bg-card/95 px-8 py-10 text-center shadow-paper-lg">
      <div className="mb-5 flex items-center justify-center gap-1.5">
        {FLOW_STEPS.map((s, i) => (
          <motion.span
            key={s}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.12, ease: EASE }}
            className="flex items-center gap-1.5"
          >
            <span className="inline-flex items-center gap-1 rounded-full bg-pine-soft px-2.5 py-1 text-xs font-medium text-pine">
              <Check className="size-3" />
              {s}
            </span>
            {i < FLOW_STEPS.length - 1 && <ArrowRight className="size-3 text-taupe" />}
          </motion.span>
        ))}
      </div>
      <h2 className="font-display text-3xl font-medium tracking-tight text-foreground">
        Totul într-un singur loc
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
        Un mesaj, o comandă, o livrare, o factură. Așa lucrează Exemplu SRL —{' '}
        <span className="font-display italic text-amber-ink">de la mesaj la livrare.</span>
      </p>
    </div>
  )
}

// ===========================================================================
//  Caption band + controls
// ===========================================================================
export interface CinematicCaptionProps {
  /** 1..7 chapter index (see CINEMATIC_CHAPTERS) */
  chapter: number
  title: string
  text: string
  paused: boolean
  onTogglePause: () => void
  onSkip: () => void
  onClose: () => void
}

function ControlButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid size-9 place-items-center rounded-full border border-hairline bg-card text-ink-soft shadow-paper transition-colors hover:bg-accent hover:text-foreground cursor-pointer"
    >
      {children}
    </button>
  )
}

export function CinematicCaption({
  chapter,
  title,
  text,
  paused,
  onTogglePause,
  onSkip,
  onClose,
}: CinematicCaptionProps) {
  const total = CINEMATIC_CHAPTERS.length
  const idx = Math.min(Math.max(chapter, 1), total)
  const label = CINEMATIC_CHAPTERS[idx - 1] ?? ''

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[95] flex justify-center px-3 pb-4 sm:pb-6"
    >
      <div className="pointer-events-auto relative w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-card/95 shadow-paper-lg backdrop-blur">
        {/* overall progress */}
        <motion.div
          initial={false}
          animate={{ width: `${(idx / total) * 100}%` }}
          transition={{ duration: 0.6, ease: EASE }}
          className="absolute inset-x-0 top-0 h-[3px] bg-amber"
          style={{ transformOrigin: 'left' }}
        />

        <div className="flex items-center gap-3.5 px-4 py-3.5 sm:px-5">
          {/* chapter index */}
          <div className="hidden shrink-0 flex-col items-center sm:flex" aria-hidden>
            <span className="font-display text-2xl font-semibold leading-none text-amber-ink nums">
              {pad2(idx)}
            </span>
            <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-taupe">
              / {pad2(total)}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-amber-ink">
                Pas {idx}/{total} · {label}
              </span>
              <div className="flex items-center gap-1" aria-hidden>
                {CINEMATIC_CHAPTERS.map((c, i) => (
                  <span
                    key={c}
                    className={cn(
                      'h-1 rounded-full transition-all duration-500',
                      i < idx - 1
                        ? 'w-3 bg-amber/70'
                        : i === idx - 1
                          ? 'w-4 bg-amber'
                          : 'w-3 bg-hairline',
                    )}
                  />
                ))}
              </div>
            </div>
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 6, filter: 'blur(3px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.32, ease: EASE }}
            >
              <h3 className="font-display text-[1.05rem] font-medium leading-tight text-foreground">
                {title}
              </h3>
              <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{text}</p>
            </motion.div>
          </div>

          {/* controls */}
          <div className="flex shrink-0 items-center gap-1.5">
            <ControlButton label={paused ? 'Reia simularea' : 'Pauză'} onClick={onTogglePause}>
              {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
            </ControlButton>
            <ControlButton label="Sari peste" onClick={onSkip}>
              <SkipForward className="size-4" />
            </ControlButton>
            <ControlButton label="Închide simularea" onClick={onClose}>
              <X className="size-4" />
            </ControlButton>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
